import { useMemo } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Stack } from "@fluentui/react/lib/Stack";

import { Merge } from "Components/Generic/Merge";
import { IMergeEntryProps } from "Components/Generic/Merge/MergeEntry";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { getItemType } from "Utils/Item";
import { useProjectItem } from "../../Hooks/UseProjectItem";
import { WithProjectItemContext } from "../../ProjectItemContext/WithProjectItemContext";
import { DC_RESULT_COLUMN_LIST } from "../DuplicateCheckColums";
import { useResult } from "../MergeContext/UseResult";
import { WithMergeContext } from "../MergeContext/WithMergeContext";
import { IMergeContentProps } from "./IMergeContentProps";
import { useMergeContentStyles } from "./UseMergeContentStyles";

type CustomIMergeEntryProps = Omit<IMergeEntryProps, "onChange" | "translator" | "index" | "config" | "isConfigReady">;

export const MergeContent = WithTheme(
  WithProjectItemContext(
    WithMergeContext(
      WithTranslator((props: IMergeContentProps): JSX.Element => {
        const {
          projectId,
          projectItemId,
          selectedResultItemId,
          onMergeChange,
          onPrevious,
          onValidate,
          mergeSelectionMap,
          translator,
          theme: { spacing }
        } = props;

        const { projectItem } = useProjectItem(projectId, projectItemId);
        const result = useResult(selectedResultItemId);
        const styles = useMergeContentStyles();

        const entries = useMemo(
          () => DC_RESULT_COLUMN_LIST.map<CustomIMergeEntryProps>((fieldName) => {
            const sourceValue = projectItem?.[fieldName];
            const targetValue = result?.[fieldName];

            if (sourceValue == null && targetValue != null) {
              onMergeChange(`target.${fieldName}`, targetValue);
              // trigger change on target value if source value is null
              // this way we can inform the parent about automatic value
              // selection
            }

            return {
              fieldName,
              entityType: getItemType(projectItem),
              sourceItem: projectItem,
              targetItem: result,
              selectedKey: mergeSelectionMap.get(fieldName) ?? `source`,
              allowDisableValue: false
            };
          }),
          [mergeSelectionMap, onMergeChange, projectItem, result]
        );

        return (
          <Stack tokens={{ childrenGap: spacing.m }} className={styles.root}>
            <Stack.Item className={styles.grid}>
              <Merge
                entries={entries}
                onChange={onMergeChange}
                alternativeSourceToken="dc.merge.current"
                alternativeTargetToken="dc.merge.duplicate"
              />
            </Stack.Item>
            <Stack horizontal disableShrink horizontalAlign="end" tokens={{ childrenGap: spacing.s1 }}>
              <DefaultButton onClick={onPrevious} iconProps={{ iconName: `ChevronLeft` }}>
                {translator.get(`generic.previous`, `Previous`)}
              </DefaultButton>
              <PrimaryButton onClick={onValidate}>{translator.get(`generic.next`, `Next`)}</PrimaryButton>
            </Stack>
          </Stack>
        );
      })
    )
  )
);
